import i18n from '../_mesomb/i18n';
import { parseExternalPaymentLink } from '../app/external-payment-link.mjs';
import axios from '../app/api-client.ts';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { buildConfigs, extract } from '../_mesomb/utils';
import CheckoutPaymentPage from '../app/modules/public/CheckoutPaymentPage.tsx';
import FundContributePage from '../app/modules/public/FundContributePage.tsx';
import PayWithLinkPage from '../app/modules/public/PayWithLinkPage.tsx';
import SecurePaymentPage from '../app/modules/public/SecurePaymentPage.tsx';
import { ApplicationModel, FundModel, PaymentLinkModel } from '../types';

const App = () => {
  const invalidLink = <div className='p-10' role='alert'>{i18n.t('This payment link is invalid or unavailable. Please request a new link from the merchant.')}</div>;
  const router = createBrowserRouter([
    {
      path: '/l/:key/',
      element: <PayWithLinkPage />,
      loader: async () => {
        const [{ data: configs }, { data: pricing }] = await Promise.all([
          axios.get<Parameters<typeof buildConfigs>[0]>(`/configs/`),
          axios.get<import("../types").AppConfig["payment_pricing"]>(`/configs/?filter=payment_pricing`),
        ]);
        return {
          configs: buildConfigs({ ...configs, payment_pricing: pricing }),
        };
      },
    },
    {
      path: '/f/:key/*',
      element: <FundContributePage />,
      loader: async ({ params }) => {
        const key = extract(params, 'key');
        const { data } = await axios.get<FundModel>(`/fund/${key}/contribute/`);
        return { fund: data };
      },
    },
    {
      path: '/e/:key/*',
      errorElement: invalidLink,
      element: <PayWithLinkPage />,
      loader: async ({ params }) => {
        const externalKey = extract(params, 'key');
        if(params['*']) throw new Response('Invalid payment link', {status:400});
        let parsed;
        try { parsed = parseExternalPaymentLink(externalKey); }
        catch { throw new Response(i18n.t('This payment link is invalid or unavailable. Please request a new link from the merchant.'), {status:400}); }
        const { data } = await axios.get<ApplicationModel>(
          `/payment/external/${encodeURIComponent(parsed.key)}/`
        );
        const [{data:configs},{data:pricing}] = await Promise.all([
          axios.get('/configs/'), axios.get('/configs/?filter=payment_pricing'),
        ]);
        return {
          configs:buildConfigs({...configs,payment_pricing:pricing}),
          link: {
            ...parsed,
            nonce: parsed.nonce,
            currency: parsed.currency,
            type: 'PAYMENT',
            application: data,
            call_to_action: 'pay',
            amount: {
              value: parsed.amount,
              currency: parsed.currency || 'XAF',
              editable: false,
            },
            price: parsed.amount,
            description: parsed.desc,
            title: parsed.title,
            source: 'external',
            key: data.key,
            data: parsed,
          },
        };
      },
    },
    {
      path: '/s/:token',
      element: <SecurePaymentPage />,
    },
    {
      path: '/c/:key/*',
      element: <CheckoutPaymentPage />,
      loader: async () => {
        const [{ data: configs }, { data: pricing }] = await Promise.all([
          axios.get<Parameters<typeof buildConfigs>[0]>(`/configs/`),
          axios.get<import("../types").AppConfig["payment_pricing"]>(`/configs/?filter=payment_pricing`),
        ]);
        return {
          configs: buildConfigs({ ...configs, payment_pricing: pricing }),
        };
      },
    },
    {
      path: '/checkout/:key/*',
      element: <CheckoutPaymentPage />,
      loader: async () => {
        const [{ data: configs }, { data: pricing }] = await Promise.all([
          axios.get<Parameters<typeof buildConfigs>[0]>(`/configs/`),
          axios.get<import("../types").AppConfig["payment_pricing"]>(`/configs/?filter=payment_pricing`),
        ]);
        return {
          configs: buildConfigs({ ...configs, payment_pricing: pricing }),
        };
      },
    },
  ]);

  return <RouterProvider router={router} />;
};

export default App;
